<script lang="ts">
  import { AatmanovaClient } from '@aatmanova/sdk';
  import {
    createQuery,
    createMutation,
    useQueryClient
  } from '@tanstack/svelte-query';
  import { derived, readable } from 'svelte/store';

  import LoginGate from './components/auth/LoginGate.svelte';
  import Sidebar from './components/layout/Sidebar.svelte';
  import Header from './components/layout/Header.svelte';
  import OverviewTab from './components/overview/OverviewTab.svelte';
  import CatalogTab from './components/catalog/CatalogTab.svelte';
  import OrdersTab from './components/orders/OrdersTab.svelte';
  import QuotesTab from './components/quotes/QuotesTab.svelte';
  import CmsTab from './components/cms/CmsTab.svelte';
  import ContactsTab from './components/contacts/ContactsTab.svelte';
  import ProductDrawer from './components/catalog/ProductDrawer.svelte';
  import CategoryModal from './components/catalog/CategoryModal.svelte';
  import ToastStack from './components/ToastStack.svelte';

  let { client }: { client: AatmanovaClient } = $props();

  const queryClient = useQueryClient();

  // Queries
  const userQuery = createQuery({
    queryKey: ['user'],
    queryFn: () => client.getCurrentUser(),
    retry: false
  });

  const currentUser = $derived($userQuery.data?.data?.user);

  const productsQuery = createQuery(
    derived(userQuery, ($userQuery) => ({
      queryKey: ['products'],
      queryFn: () => client.getProducts({ limit: 1000 }),
      enabled: !!$userQuery.data?.data?.user
    }))
  );

  const categoriesQuery = createQuery(
    derived(userQuery, ($userQuery) => ({
      queryKey: ['categories'],
      queryFn: () => client.getCategories(),
      enabled: !!$userQuery.data?.data?.user
    }))
  );

  // Date range filter
  let timeRange = $state(30);

  function signalToStore<T>(getter: () => T) {
    return readable(getter(), (set) => {
      const cleanup = $effect.root(() => {
        $effect(() => {
          set(getter());
        });
      });
      return cleanup;
    });
  }

  // Health query
  const healthQueryStore = signalToStore(() => ({
    queryKey: ['admin_health'],
    queryFn: () => client.getSystemHealth(),
    enabled: !!currentUser,
    refetchInterval: 30000
  }));
  const healthQuery = createQuery(healthQueryStore);

  // Admin stats query
  const adminStatsQueryStore = signalToStore(() => ({
    queryKey: ['admin_stats', timeRange],
    queryFn: () => client.getAdminStats(timeRange),
    enabled: !!currentUser
  }));
  const adminStatsQuery = createQuery(adminStatsQueryStore);

  // Orders state for server-side pagination
  let ordersPage = $state(1);
  let ordersLimit = $state(10);
  let orderStatus = $state('ALL');

  const ordersQueryStore = signalToStore(() => ({
    queryKey: ['orders', ordersPage, ordersLimit, orderStatus],
    queryFn: () => client.getOrders({ page: ordersPage, limit: ordersLimit, status: orderStatus === 'ALL' ? undefined : orderStatus }),
    enabled: !!currentUser
  }));
  const ordersQuery = createQuery(ordersQueryStore);

  // Reset orders page when filters change
  $effect(() => {
    if (orderStatus !== 'ALL') ordersPage = 1;
  });

  const quotesQuery = createQuery(
    derived(userQuery, ($userQuery) => ({
      queryKey: ['admin_quotes'],
      queryFn: async () => {
        const res = await client.getQuotes();
        return res.data || [];
      },
      enabled: !!$userQuery.data?.data?.user
    }))
  );

  // Toast notifications state
  interface ToastItem {
    id: string;
    message: string;
    type: 'info' | 'success' | 'warning' | 'error';
  }
  let toasts = $state<ToastItem[]>([]);

  function addToast(message: string, type: ToastItem['type'] = 'success') {
    const id = Math.random().toString(36).substring(2, 9);
    toasts = [...toasts, { id, message, type }];
    setTimeout(() => {
      toasts = toasts.filter(t => t.id !== id);
    }, 3000);
  }

  // Mutations
  const loginMutation = createMutation({
    mutationFn: (credentials: any) => client.login(credentials),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      addToast('Successfully authenticated admin session.', 'success');
    },
    onError: (err: any) => {
      addToast(err.message || 'Authentication failed.', 'error');
    }
  });

  const logoutMutation = createMutation({
    mutationFn: () => client.logout(),
    onSuccess: () => {
      queryClient.setQueryData(['user'], null);
      queryClient.invalidateQueries({ queryKey: ['user'] });
      addToast('Admin session logged out.', 'info');
    }
  });

  const deleteProductMutation = createMutation({
    mutationFn: (id: string) => client.deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      addToast('Product successfully archived.', 'success');
    },
    onError: (err: any) => {
      addToast(err.message || 'Failed to archive product.', 'error');
    }
  });

  const saveProductMutation = createMutation({
    mutationFn: (payload: { id?: string; data: any }) => {
      if (payload.id) {
        return client.updateProduct(payload.id, payload.data);
      } else {
        return client.createProduct(payload.data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      isFormOpen = false;
      resetForm();
      addToast(formMode === 'create' ? 'Spiritual product created successfully.' : 'Configurations updated successfully.', 'success');
    },
    onError: (err: any) => {
      addToast(err.message || 'Failed to save product details.', 'error');
    }
  });

  const createCategoryMutation = createMutation({
    mutationFn: (data: any) => client.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      isCategoryFormOpen = false;
      categoryName = '';
      categorySlug = '';
      addToast('New product category created.', 'success');
    },
    onError: (err: any) => {
      addToast(err.message || 'Failed to create category.', 'error');
    }
  });

  const updateCategoryMutation = createMutation({
    mutationFn: (payload: { id: string; data: any }) => client.updateCategory(payload.id, payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      addToast('Product category updated.', 'success');
    },
    onError: (err: any) => {
      addToast(err.message || 'Failed to update category.', 'error');
    }
  });

  const deleteCategoryMutation = createMutation({
    mutationFn: (id: string) => client.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      addToast('Product category deleted.', 'success');
    },
    onError: (err: any) => {
      addToast(err.message || 'Failed to delete category.', 'error');
    }
  });

  const createSubcategoryMutation = createMutation({
    mutationFn: (data: any) => client.createSubcategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      addToast('New subcategory created.', 'success');
    },
    onError: (err: any) => {
      addToast(err.message || 'Failed to create subcategory.', 'error');
    }
  });

  const updateSubcategoryMutation = createMutation({
    mutationFn: (payload: { id: string; data: any }) => client.updateSubcategory(payload.id, payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      addToast('Subcategory updated.', 'success');
    },
    onError: (err: any) => {
      addToast(err.message || 'Failed to update subcategory.', 'error');
    }
  });

  const deleteSubcategoryMutation = createMutation({
    mutationFn: (id: string) => client.deleteSubcategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      addToast('Subcategory deleted.', 'success');
    },
    onError: (err: any) => {
      addToast(err.message || 'Failed to delete subcategory.', 'error');
    }
  });

  // Authentication States
  let email = $state('');
  let password = $state('');
  let loginError = $state('');

  // Tab State initialized from URL pathname
  let activeTab = $state(
    (typeof window !== 'undefined' && ['overview', 'catalog', 'orders', 'quotes', 'cms', 'contacts'].includes(window.location.pathname.replace(/^\/|\/$/g, '')))
      ? window.location.pathname.replace(/^\/|\/$/g, '')
      : 'overview'
  );

  // Sync tab state changes to URL pathname
  $effect(() => {
    if (typeof window !== 'undefined') {
      const currentTab = window.location.pathname.replace(/^\/|\/$/g, '') || 'overview';
      if (currentTab !== activeTab) {
        window.history.pushState({}, '', '/' + activeTab);
      }
    }
  });

  // Listen to popstate (browser back/forward button clicks)
  $effect(() => {
    if (typeof window !== 'undefined') {
      const handlePopState = () => {
        const tab = window.location.pathname.replace(/^\/|\/$/g, '') || 'overview';
        if (['overview', 'catalog', 'orders', 'quotes', 'cms', 'contacts'].includes(tab)) {
          activeTab = tab;
        }
      };
      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }
  });

  // Dynamic UI theme state
  let theme = $state(localStorage.getItem('aatmanova_theme') || 'dark');

  $effect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('aatmanova_theme', theme);
  });

  function toggleTheme() {
    theme = theme === 'dark' ? 'light' : 'dark';
  }

  // Dashboard States
  let searchQuery = $state('');
  let selectedCategoryFilter = $state('');
  let isFormOpen = $state(false);
  let formMode = $state<'create' | 'edit'>('create');
  let isCategoryFormOpen = $state(false);

  // Lock body scroll when product drawer is open to prevent background scrolling
  $effect(() => {
    if (typeof document !== 'undefined') {
      if (isFormOpen) {
        document.body.classList.add('overflow-hidden');
      } else {
        document.body.classList.remove('overflow-hidden');
      }
    }
  });

  // Product Form States
  let formId = $state('');
  let formSku = $state('');
  let formSlug = $state('');
  let formName = $state('');
  let formDescription = $state('');
  let formRetailPrice = $state(0);
  let formOfferPrice = $state(0);
  let formWholesalePrice = $state<number | null>(null);
  let formCostPrice = $state<number | null>(null);
  let formStock = $state(0);
  let formStatus = $state<'DRAFT' | 'ACTIVE' | 'ARCHIVED'>('DRAFT');
  let formCategoryId = $state('');
  let formSubcategoryId = $state('');
  let formMediaUrl = $state('');
  let uploadingFile = $state(false);
  let formUnit = $state('Piece');
  let formIsFinalPrepped = $state(false);
  let formIsBuyEnabled = $state(true);
  let formIsQuoteEnabled = $state(true);
  let formLowStockThreshold = $state(10);
  let formHsnCode = $state('');
  let formTags = $state('');
  let formCareGuide = $state('');
  let formSourcingInfo = $state('');
  let formSeoTitle = $state('');
  let formSeoDescription = $state('');
  let formSpecification = $state('{}');
  let formMediaList = $state<{ url: string; altText?: string; sortOrder: number }[]>([]);

  // Category Form States
  let categoryName = $state('');
  let categorySlug = $state('');


  // Stats derivation
  const stats = $derived.by(() => {
    const orders = $ordersQuery.data?.data || [];
    const products = $productsQuery.data?.products || [];
    
    const totalRevenue = orders.reduce((sum: number, o: any) => sum + Number(o.total), 0);
    const totalOrdersCount = orders.length;
    const totalProductsCount = products.length;
    const lowStockCount = products.filter((p: any) => p.stock < (p.lowStockThreshold ?? 10)).length;

    return {
      totalRevenue,
      totalOrdersCount,
      totalProductsCount,
      lowStockCount
    };
  });

  // Sales data for forecast chart
  const salesChartData = $derived.by(() => {
    const rawSalesData = $adminStatsQuery.data?.salesData || [];
    return rawSalesData.map((d: any) => ({
      label: d.date,
      actual: d.actual,
      forecast: d.actual * 1.1,
    }));
  });

  // Health data
  const healthData = $derived($healthQuery.data || null);

  // Hotspot data (static Indian region clusters)
  const hotspots = [
    { region: 'Northeast', x: 220, y: 70, ordersCount: 0, popularCategory: 'Puja Items' },
    { region: 'North', x: 190, y: 55, ordersCount: 0, popularCategory: 'Rudraksha' },
    { region: 'West', x: 100, y: 90, ordersCount: 0, popularCategory: 'Incense' },
    { region: 'South', x: 150, y: 140, ordersCount: 0, popularCategory: 'Crystals' },
    { region: 'East', x: 210, y: 100, ordersCount: 0, popularCategory: 'Books' },
  ].map(h => ({
    ...h,
    ordersCount: (stats.totalOrdersCount || 0) > 0 ? Math.floor(Math.random() * 50 + 10) : 0,
  }));

  // Recent operational activity logs
  const recentActivities = $derived.by(() => {
    const orders = $ordersQuery.data?.data || [];
    const products = $productsQuery.data?.products || [];
    const list: any[] = [];

    orders.forEach((o: any) => {
      list.push({
        type: 'order',
        title: `Transaction Registered`,
        desc: `Order #${o.id.slice(-6).toUpperCase()} by ${o.user?.name || 'Guest'} for Rs. ${Number(o.total).toFixed(2)}`,
        time: o.createdAt,
      });
    });

    products.forEach((p: any) => {
      if (p.stock < (p.lowStockThreshold ?? 10)) {
        list.push({
          type: 'warning',
          title: `Low Stock Warning`,
          desc: `${p.name} (${p.sku}) is down to ${p.stock} units.`,
          time: p.updatedAt || p.createdAt,
        });
      }
    });

    return list
      .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
      .slice(0, 5);
  });

  // Derived filtered products list
  const filteredProducts = $derived.by(() => {
    const items = $productsQuery.data?.products || [];
    const query = searchQuery.trim().toLowerCase();
    
    if (!query) {
      return items.filter((p: any) => {
        return selectedCategoryFilter ? p.categoryId === selectedCategoryFilter : true;
      });
    }

    const tokens = query.split(/\s+/).filter(Boolean);

    return items.filter((p: any) => {
      const matchesCategory = selectedCategoryFilter ? p.categoryId === selectedCategoryFilter : true;
      if (!matchesCategory) return false;

      const name = (p.name || '').toLowerCase();
      const sku = (p.sku || '').toLowerCase();
      const slug = (p.slug || '').toLowerCase();
      const hsnCode = (p.hsnCode || '').toLowerCase();
      const description = (p.description || '').toLowerCase();
      const categoryName = (p.category?.name || '').toLowerCase();
      const subcategoryName = (p.subcategory?.name || '').toLowerCase();
      const status = (p.status || '').toLowerCase();

      return tokens.every(token => {
        return name.includes(token) ||
               sku.includes(token) ||
               slug.includes(token) ||
               hsnCode.includes(token) ||
               description.includes(token) ||
               categoryName.includes(token) ||
               subcategoryName.includes(token) ||
               status.includes(token);
      });
    });
  });

  function handleLogin(e: SubmitEvent) {
    e.preventDefault();
    loginError = '';
    $loginMutation.mutate(
      { email, password },
      {
        onError: (err: any) => {
          loginError = err.message || 'Invalid email or password';
        }
      }
    );
  }

  function openCreate() {
    formMode = 'create';
    resetForm();
    if ($categoriesQuery.data && $categoriesQuery.data.length > 0) {
      formCategoryId = $categoriesQuery.data[0].id;
    }
    isFormOpen = true;
  }

  function openEdit(product: any) {
    formMode = 'edit';
    formId = product.id;
    formSku = product.sku;
    formSlug = product.slug;
    formName = product.name;
    formDescription = product.description;
    formRetailPrice = Number(product.retailPrice);
    formOfferPrice = Number(product.offerPrice);
    formWholesalePrice = product.wholesalePrice ? Number(product.wholesalePrice) : null;
    formCostPrice = product.costPrice ? Number(product.costPrice) : null;
    formStock = product.stock;
    formStatus = product.status;
    formCategoryId = product.categoryId;
    formSubcategoryId = product.subcategoryId || '';
    formMediaUrl = product.media && product.media[0] ? product.media[0].url : '';
    
    formUnit = product.unit || 'Piece';
    formIsFinalPrepped = !!product.isFinalPrepped;
    formIsBuyEnabled = product.isBuyEnabled !== false;
    formIsQuoteEnabled = product.isQuoteEnabled !== false;
    formLowStockThreshold = product.lowStockThreshold ?? 10;
    formHsnCode = product.hsnCode || '';
    formTags = product.tags ? product.tags.join(', ') : '';
    formCareGuide = product.careGuide || '';
    formSourcingInfo = product.sourcingInfo || '';
    formSeoTitle = product.seoMeta?.title || '';
    formSeoDescription = product.seoMeta?.description || '';
    formSpecification = product.specification ? JSON.stringify(product.specification, null, 2) : '{}';
    formMediaList = product.media ? product.media.map((m: any, i: number) => ({ url: m.url, altText: m.altText || '', sortOrder: m.sortOrder || i })) : [];
    
    isFormOpen = true;
  }

  function resetForm() {
    formId = '';
    formSku = '';
    formSlug = '';
    formName = '';
    formDescription = '';
    formRetailPrice = 0;
    formOfferPrice = 0;
    formWholesalePrice = null;
    formCostPrice = null;
    formStock = 0;
    formStatus = 'DRAFT';
    formCategoryId = '';
    formSubcategoryId = '';
    formMediaUrl = '';
    
    formUnit = 'Piece';
    formIsFinalPrepped = false;
    formIsBuyEnabled = true;
    formIsQuoteEnabled = true;
    formLowStockThreshold = 10;
    formHsnCode = '';
    formTags = '';
    formCareGuide = '';
    formSourcingInfo = '';
    formSeoTitle = '';
    formSeoDescription = '';
    formSpecification = '{}';
    formMediaList = [];
  }

  async function handleFileUpload(e: Event) {
    const target = e.target as HTMLInputElement;
    if (!target.files || target.files.length === 0) return;

    const file = target.files[0];
    uploadingFile = true;

    try {
      const res = await client.uploadMedia(file);
      if (res && res.url) {
        formMediaUrl = res.url;
        // Also push it to the media list gallery
        formMediaList = [...formMediaList, { url: res.url, altText: file.name, sortOrder: formMediaList.length }];
      }
    } catch (err: any) {
      alert(err.message || 'File upload failed');
    } finally {
      uploadingFile = false;
    }
  }

  function handleSaveProduct(e: SubmitEvent) {
    e.preventDefault();
    
    const tags = formTags ? formTags.split(',').map(t => t.trim()).filter(Boolean) : [];
    
    let parsedSpecs = {};
    if (formSpecification && formSpecification.trim() !== '') {
      try {
        parsedSpecs = JSON.parse(formSpecification);
      } catch (err) {
        addToast('Invalid JSON in specifications. Please fix format.', 'error');
        return;
      }
    }

    const payloadData: any = {
      slug: formSlug,
      name: formName,
      description: formDescription,
      retailPrice: Number(formRetailPrice),
      offerPrice: Number(formOfferPrice),
      wholesalePrice: formWholesalePrice !== null && formWholesalePrice !== undefined && String(formWholesalePrice).trim() !== '' ? Number(formWholesalePrice) : null,
      costPrice: formCostPrice !== null && formCostPrice !== undefined && String(formCostPrice).trim() !== '' ? Number(formCostPrice) : null,
      stock: Number(formStock),
      status: formStatus,
      categoryId: formCategoryId,
      subcategoryId: formSubcategoryId ? formSubcategoryId : null,
      unit: formUnit,
      isFinalPrepped: formIsFinalPrepped,
      isBuyEnabled: formIsBuyEnabled,
      isQuoteEnabled: formIsQuoteEnabled,
      lowStockThreshold: Number(formLowStockThreshold),
      hsnCode: formHsnCode || null,
      tags,
      careGuide: formCareGuide || null,
      sourcingInfo: formSourcingInfo || null,
      seoMeta: {
        title: formSeoTitle || '',
        description: formSeoDescription || '',
        keywords: [],
        snippet: '',
        canonical: '',
        ogImage: ''
      },
      specification: parsedSpecs,
    };

    if (formMode === 'create') {
      payloadData.sku = formSku;
    }

    if (formMediaList.length > 0) {
      payloadData.media = formMediaList.map((m, i) => ({
        url: m.url,
        altText: m.altText || formName,
        sortOrder: m.sortOrder ?? i
      }));
    } else if (formMediaUrl) {
      payloadData.media = [{
        url: formMediaUrl,
        altText: formName,
        sortOrder: 0
      }];
    }

    $saveProductMutation.mutate({
      id: formMode === 'edit' ? formId : undefined,
      data: payloadData
    });
  }

  function handleSaveCategory(e: SubmitEvent) {
    e.preventDefault();
    $createCategoryMutation.mutate({
      name: categoryName,
      slug: categorySlug
    });
  }

  let confirmDeleteId = $state<string | null>(null);

  function handleDelete(id: string) {
    confirmDeleteId = id;
  }

  function confirmDelete() {
    if (confirmDeleteId) {
      $deleteProductMutation.mutate(confirmDeleteId);
      confirmDeleteId = null;
    }
  }


</script>

<main class="min-h-screen bg-ambient-flow text-base-content flex flex-col font-sans">
  {#if $userQuery.isLoading}
    <div class="flex-1 flex flex-col items-center justify-center gap-4">
      <div class="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
      <p class="text-base-content/75 font-medium animate-pulse">Loading secure administrator session...</p>
    </div>
  {:else if !currentUser}
    <LoginGate
      bind:email={email}
      bind:password={password}
      loginError={loginError}
      isPending={$loginMutation.isPending}
      onsubmit={handleLogin}
    />
  {:else}
    <!-- Responsive Drawer Wrapper -->
    <div class="drawer lg:drawer-open flex-1 min-h-screen">
      <input id="admin-drawer" type="checkbox" class="drawer-toggle" />

      <!-- Main Panel Context (Page content here) -->
      <div class="drawer-content flex-1 flex flex-col min-w-0 overflow-y-auto">
        <Header {theme} {toggleTheme} drawerId="admin-drawer" />

        <!-- Main Panel Content View -->
        <div class="flex-1 p-4 md:p-8 space-y-8 max-w-7xl w-full mx-auto">
          <!-- Date Range Filter Bar -->
          <div class="flex items-center gap-2 p-1 bg-base-200 rounded-xl border border-base-300 self-start">
            {#each [7, 30, 90, 365] as days}
              <button
                onclick={() => { timeRange = days; if (activeTab === 'overview') ordersPage = 1; }}
                class="px-3 py-1.5 rounded-lg text-xs font-bold transition-all {timeRange === days ? 'bg-accent text-accent-content shadow' : 'hover:bg-base-300 text-base-content/70'}"
              >
                {days === 365 ? '1 Year' : `${days} Days`}
              </button>
            {/each}
          </div>

          {#if activeTab === 'overview'}
            <OverviewTab stats={stats} recentActivities={recentActivities} salesData={salesChartData} monthlyGoal={500000} hotspots={hotspots} {healthData} />
           {:else if activeTab === 'catalog'}
            <CatalogTab
              client={client}
              addToast={addToast}
              invalidateProducts={() => {
                queryClient.invalidateQueries({ queryKey: ['products'] });
                queryClient.invalidateQueries({ queryKey: ['categories'] });
              }}
              bind:searchQuery={searchQuery}
              bind:selectedCategoryFilter={selectedCategoryFilter}
              categories={$categoriesQuery.data || []}
              filteredProducts={filteredProducts}
              productsQueryIsLoading={$productsQuery.isLoading}
              openCreateProduct={openCreate}
              openEditProduct={openEdit}
              handleDeleteProduct={handleDelete}
              handleCreateCategory={(data) => $createCategoryMutation.mutateAsync(data)}
              handleUpdateCategory={(id, data) => $updateCategoryMutation.mutateAsync({ id, data })}
              handleDeleteCategory={(id) => $deleteCategoryMutation.mutateAsync(id)}
              handleCreateSubcategory={(data) => $createSubcategoryMutation.mutateAsync(data)}
              handleUpdateSubcategory={(id, data) => $updateSubcategoryMutation.mutateAsync({ id, data })}
              handleDeleteSubcategory={(id) => $deleteSubcategoryMutation.mutateAsync(id)}
            />
          {:else if activeTab === 'orders'}
            <OrdersTab
              orders={$ordersQuery.data?.data || []}
              ordersQueryIsLoading={$ordersQuery.isLoading}
              ordersMeta={$ordersQuery.data?.meta}
              client={client}
              addToast={addToast}
              invalidateOrders={() => queryClient.invalidateQueries({ queryKey: ['orders'] })}
              bind:page={ordersPage}
              bind:limit={ordersLimit}
              bind:orderStatus={orderStatus}
            />
          {:else if activeTab === 'quotes'}
            <QuotesTab
              client={client}
              addToast={addToast}
            />
          {:else if activeTab === 'contacts'}
            <ContactsTab {client} {addToast} />
          {:else if activeTab === 'cms'}
            <CmsTab {addToast} />
          {/if}
        </div>
      </div>

      <!-- Drawer Side (Sidebar here) -->
      <div class="drawer-side z-40 lg:overflow-visible">
        <label for="admin-drawer" aria-label="close sidebar" class="drawer-overlay"></label>
        <Sidebar
          bind:activeTab={activeTab}
          currentUser={currentUser}
          lowStockCount={stats.lowStockCount}
          ordersCount={$ordersQuery.data?.meta?.total || $ordersQuery.data?.data?.length || 0}
          quotesCount={$quotesQuery.data?.length || 0}
          onlogout={() => $logoutMutation.mutate()}
          drawerId="admin-drawer"
          theme={theme}
        />
      </div>
    </div>

    <!-- Product Drawer/Form Modal -->
    <ProductDrawer
      bind:isFormOpen={isFormOpen}
      formMode={formMode}
      formId={formId}
      bind:formSku={formSku}
      bind:formSlug={formSlug}
      bind:formName={formName}
      bind:formDescription={formDescription}
      bind:formRetailPrice={formRetailPrice}
      bind:formOfferPrice={formOfferPrice}
      bind:formWholesalePrice={formWholesalePrice}
      bind:formCostPrice={formCostPrice}
      bind:formStock={formStock}
      bind:formStatus={formStatus}
      bind:formCategoryId={formCategoryId}
      bind:formSubcategoryId={formSubcategoryId}
      bind:formMediaUrl={formMediaUrl}
      bind:uploadingFile={uploadingFile}
      bind:formUnit={formUnit}
      bind:formIsFinalPrepped={formIsFinalPrepped}
      bind:formIsBuyEnabled={formIsBuyEnabled}
      bind:formIsQuoteEnabled={formIsQuoteEnabled}
      bind:formLowStockThreshold={formLowStockThreshold}
      bind:formHsnCode={formHsnCode}
      bind:formTags={formTags}
      bind:formCareGuide={formCareGuide}
      bind:formSourcingInfo={formSourcingInfo}
      bind:formSeoTitle={formSeoTitle}
      bind:formSeoDescription={formSeoDescription}
      bind:formSpecification={formSpecification}
      bind:formMediaList={formMediaList}
      categories={$categoriesQuery.data || []}
      client={client}
      saveProductMutationIsPending={$saveProductMutation.isPending}
      handleFileUpload={handleFileUpload}
      handleSaveProduct={handleSaveProduct}
    />

    <!-- Add Category Modal -->
    <CategoryModal
      bind:isCategoryFormOpen={isCategoryFormOpen}
      bind:categoryName={categoryName}
      bind:categorySlug={categorySlug}
      createCategoryMutationIsPending={$createCategoryMutation.isPending}
      handleSaveCategory={handleSaveCategory}
    />

    <!-- Custom DaisyUI Confirm Delete Modal -->
    {#if confirmDeleteId}
      <dialog class="modal modal-open z-50">
        <div class="modal-box bg-base-100 border border-base-300 shadow-2xl">
          <h3 class="font-bold text-lg text-base-content">Delete Spiritual Product</h3>
          <p class="py-4 text-sm text-base-content/70">Are you sure you want to archive this product? This will perform a soft-delete and hide it from storefront catalog queries.</p>
          <div class="modal-action">
            <button onclick={() => confirmDeleteId = null} class="btn btn-outline btn-sm font-semibold">Cancel</button>
            <button onclick={confirmDelete} class="btn btn-error btn-sm font-bold text-white">Delete</button>
          </div>
        </div>
        <button class="modal-backdrop bg-black/60 cursor-default" onclick={() => confirmDeleteId = null} aria-label="Close dialog"></button>
      </dialog>
    {/if}
  {/if}

  <!-- Toast Notification Stack -->
  <ToastStack {toasts} />
</main>
